import classnames from 'classnames';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import analytics from 'src/lib/player/analytics';
import { DEVICE_PIXEL_RATIO, FONT } from 'src/lib/player/constants';
import { t } from 'src/lib/player/i18n';
import IconMapping from 'src/lib/player/iconmapping.json';
import { type PoiType, useStore } from 'src/lib/player/store';
import { formatDistance, noop, onlyMouseEvents, realSubstring } from 'src/lib/player/utils';
import { onClickGenerator } from 'src/lib/player/utils/onClickGenerator';
import MarkerContext, { POI_HEIGHT, POI_WIDTH } from '../Map/MarkerContext';
import { createPoiDrawInfo } from '../Map/createDrawInfos';

type Props = {
  poi: PoiType;
  onClick: (poi: PoiType, isSelected: boolean) => void;
};

function drawPOI(
  poi: PoiType,
  canvas: HTMLCanvasElement,
  tickless: boolean,
  scale: number = DEVICE_PIXEL_RATIO,
): Promise<void> {
  return new Promise((resolve) => {
    const ctx = canvas.getContext('2d');
    if (ctx == null) {
      throw new Error("Couldn't create drawing canvas");
    }

    ctx.font = FONT;
    ctx.setTransform(scale, 0, 0, scale, 0, 0);

    if (poi.icon != null) {
      const isStandardIcon = Object.keys(IconMapping).includes(poi.icon);
      let icon = poi.icon;
      if (isStandardIcon) {
        icon = tickless
          ? `/img/map-icons/${poi.icon}_tickless.svg`
          : `/img/map-icons/${poi.icon}.svg`;
      }

      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width / scale, canvas.height / scale);
        resolve();
      };
      img.src = icon;
    } else {
      const drawInfo = createPoiDrawInfo(
        poi.id,
        poi.shortName ?? realSubstring(poi.name, 0, 3),
        poi.name,
        poi.latlng,
        poi.markerColor?.hex ?? 'white',
      );
      drawInfo.tickless = tickless;
      MarkerContext.draw(drawInfo, ctx, 0, 0, 1);
      resolve();
    }
  });
}

export function PoiIconSVG({
  poi,
  tickless = false,
}: {
  poi: PoiType;
  tickless?: boolean;
}): JSX.Element {
  const [poiImageUrl, setPoiImageUrl] = useState('');

  useEffect(() => {
    (async () => {
      const canvas = document.createElement('canvas');
      canvas.width = POI_WIDTH * DEVICE_PIXEL_RATIO;
      canvas.height = POI_HEIGHT * DEVICE_PIXEL_RATIO;
      await drawPOI(poi, canvas, tickless, DEVICE_PIXEL_RATIO);
      setPoiImageUrl(canvas.toDataURL('image/png'));
    })();
  }, [poi, tickless]);
  return <image href={poiImageUrl} width={POI_WIDTH} height={POI_HEIGHT} />;
}

export function PoiIcon({
  poi,
  tickless = false,
  scale = 0.5,
}: {
  poi: PoiType;
  tickless?: boolean;
  scale?: number;
}): JSX.Element {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas == null) return;

    drawPOI(poi, canvas, tickless, DEVICE_PIXEL_RATIO);
  }, [poi, tickless]);

  return (
    <canvas
      ref={canvasRef}
      width={POI_WIDTH * DEVICE_PIXEL_RATIO}
      height={POI_HEIGHT * DEVICE_PIXEL_RATIO}
      style={{ width: POI_WIDTH * scale, height: POI_HEIGHT * scale }}
    />
  );
}

export default function PoiListItem({ poi, onClick = noop }: Props): JSX.Element {
  const label = poi.name || t('poi');
  const toggleHidden = useStore((state) => state.actions.toggleHidden);
  const setHighlighted = useStore((state) => state.actions.setHighlighted);
  const toggleSelectedStarter = useStore((state) => state.actions.toggleSelectedStarter);

  const isSelected = useStore((state) => state.timer.selected.includes(poi.id));
  const isSelectedOrHighlighted = useStore(
    (state) => state.timer.highlighted === poi.id || state.timer.selected.includes(poi.id),
  );
  const isVisible = useStore((state) => !state.timer.hidden.has(poi.id));
  const eventNames = useStore((state) =>
    state.currentEventIndices
      .filter((i) => state.events[i].pois.some((f) => f.id === poi.id))
      .map((i) => state.events[i].shortName)
      .join(', '),
  );
  const eventCount = useStore((state) => state.currentEventIndices.length);
  const unitType = useStore((state) => state.options.unitType);

  function handleClick() {
    if (isSelectedOrHighlighted) {
      analytics.send('poi-list-item-click', 'poi-unselect');
    } else {
      analytics.send('poi-list-item-click', 'poi-select');
    }
    toggleSelectedStarter(poi.id);
    onClick(poi, !isSelected);
  }

  function handleHideClick(
    ev:
      | React.MouseEvent<HTMLElement>
      | React.KeyboardEvent<HTMLElement>
      | React.TouchEvent<HTMLElement>,
  ) {
    ev.stopPropagation();
    toggleHidden(poi.id);
  }

  return (
    <div
      className={classnames('starter-list-item', {
        selected: isSelectedOrHighlighted,
      })}
      {...onClickGenerator(handleClick)}
      onPointerEnter={onlyMouseEvents(() => {
        setHighlighted(poi.id);
      })}
      onPointerLeave={onlyMouseEvents(() => {
        setHighlighted(null);
      })}
    >
      <div>
        <PoiIcon poi={poi} tickless />
        <div className="starter-text" style={{ marginLeft: 5 }}>
          <div className="starter-name" title={label}>
            <div className="starter-name-content">{label}</div>
          </div>
          {(eventCount > 1 || (poi.offset != null && poi.offset >= 0)) && (
            <div className="starter-info">
              {eventCount > 1 && <span title={eventNames}>{eventNames}</span>}
              {poi.offset != null && poi.offset >= 0 && (
                <span>{formatDistance(poi.offset, unitType)}</span>
              )}
            </div>
          )}
        </div>
        <div className="starter-controls">
          {isSelected ? <i className="fas fa-star" /> : <i className="far fa-star" />}
          {isVisible ? (
            <i className="far fa-eye" {...onClickGenerator(handleHideClick)} />
          ) : (
            <i className="far fa-eye-slash" {...onClickGenerator(handleHideClick)} />
          )}
        </div>
      </div>
    </div>
  );
}
